import { Button, ButtonsGroup, Flex, Link, SectionMessage, Space, Spacing, Text } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TaxAiSearching } from '../../../../../../assets/images'
import { Scope } from '../../../../../../enums/scope'
import { useSegment } from '../../../../../../hooks'
import { useEmberTransitionTo } from '../../../../../../hooks/routing/useEmberTransitionTo'
import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../../../utils/isAuthorized'
import { useVatDeclaration, VatDeclarationContextProvider } from '../../../../context/vatDeclarationContext'
import { useVatDeclarations } from '../../../../context/vatDeclarationsContext'
import { useVatDeclarationsRoutes } from '../../../../context/vatDeclarationsRouteContext'
import { getCurrentVatDeclarationId } from '../../../VatDeclarationsList/utils/getCurrentVatDeclarationId'
import { SUGGESTIONS_SUPPORT_LINK } from '../../consts/suggestionsSupportLink'
import { getVatSuggestionsSourceDisplayString, VatSuggestionsSource } from '../../enums/vatSuggestionsSource'
import { useFetchSuggestions } from '../../hooks/useFetchSuggestions'
import * as Styled from './styles'

interface FoundSuggestionsBannerProps {
  source: VatSuggestionsSource
  vatDeclarationId?: string
}

export const FoundSuggestionsBanner = ({ source, vatDeclarationId }: FoundSuggestionsBannerProps) => {
  const { vatDeclarations } = useVatDeclarations()

  const calculatedVatDeclarationId = useMemo(
    () => (!vatDeclarationId ? getCurrentVatDeclarationId(vatDeclarations) : undefined),
    [vatDeclarationId, vatDeclarations],
  )
  const declarationId = vatDeclarationId || calculatedVatDeclarationId

  if (!declarationId) {
    return null
  }

  return (
    <VatDeclarationContextProvider vatDeclarationId={declarationId}>
      <FoundSuggestionsBannerContent source={source} vatDeclarationId={declarationId} />
    </VatDeclarationContextProvider>
  )
}

interface FoundSuggestionsBannerContentProps {
  source: VatSuggestionsSource
  vatDeclarationId: string
}

export const FoundSuggestionsBannerContent = ({ source, vatDeclarationId }: FoundSuggestionsBannerContentProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { transitionTo } = useEmberTransitionTo()

  const { startDate, endDate, periodText } = useVatDeclaration()
  const vatDeclarationsRoutes = useVatDeclarationsRoutes()
  const { url: suggestionsUrl } = useOrganizationViewUrl(
    vatDeclarationsRoutes.getSuggestionsMainRoute(vatDeclarationId),
  )

  const hasUpsellScope = isAuthorized(Scope.VatHelperUpsell)
  const hasWriteScope = isAuthorized(Scope.VatHelperWrite)
  const isNotAuthorized = !hasUpsellScope && !hasWriteScope

  const dates = useMemo(
    () => (startDate && endDate ? `dates:${startDate}...${endDate}` : undefined),
    [startDate, endDate],
  )

  const { isLoading, suggestions, totalSuggestions } = useFetchSuggestions({
    entryDatePeriod: dates,
  })

  const handleSuggestionsReviewClick = useCallback(() => {
    const sourceDisplayString = getVatSuggestionsSourceDisplayString(source)

    track(`xxx - taxhelper - Review Suggestions ${sourceDisplayString} Clicked (Client)`)

    if (!suggestionsUrl) {
      return
    }
    transitionTo(suggestionsUrl)
  }, [source, track, suggestionsUrl, transitionTo])

  if (suggestions.length === 0 || isLoading || isNotAuthorized) {
    return null
  }

  return (
    <>
      <SectionMessage variant="info" showIcon={false}>
        <Flex alignItems="center" gap={Spacing.S12}>
          <Styled.ImageWrapper>
            <TaxAiSearching />
          </Styled.ImageWrapper>
          <Flex alignItems="center" flexGrow={1}>
            <Flex flexDirection="column">
              <Text variant="h4">{t('copilot.found_suggestions_banner.title', { count: totalSuggestions })}</Text>
              <Text>{t('copilot.found_suggestions_banner.text', { period: periodText })}</Text>
            </Flex>

            <Styled.ButtonWrapper>
              <ButtonsGroup>
                <Link href={SUGGESTIONS_SUPPORT_LINK} target="_blank" underlined={false}>
                  {t('copilot.learn_more')}
                </Link>
                <Space />
                <Button variant="onColor" color="accent" onClick={handleSuggestionsReviewClick}>
                  {t('copilot.found_suggestions_banner.cta')}
                </Button>
              </ButtonsGroup>
            </Styled.ButtonWrapper>
          </Flex>
        </Flex>
      </SectionMessage>
      <Space />
    </>
  )
}
